import React, { useEffect, useState } from 'react';
import { X, CheckSquare, FileText, GraduationCap, ListChecks, Clock, AlertCircle } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const TaskNotificationPopup = () => {
    const { popupNotifications, dismissNotification, markAsRead } = useNotifications();
    const [currentNotification, setCurrentNotification] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (popupNotifications.length > 0 && !currentNotification) {
            // Show the first unread notification
            setCurrentNotification(popupNotifications[0]);
            setIsVisible(true);
        }
    }, [popupNotifications, currentNotification]);

    const getTaskIcon = (taskType) => {
        switch (taskType) {
            case 'quiz':
                return <FileText className="text-primary" size={24} />;
            case 'training':
                return <GraduationCap className="text-purple-500" size={24} />;
            case 'checklist':
                return <ListChecks className="text-green-500" size={24} />;
            default:
                return <CheckSquare className="text-primary" size={24} />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high':
                return 'border-l-red-500 bg-red-50';
            case 'medium':
                return 'border-l-yellow-500 bg-yellow-50';
            case 'low':
                return 'border-l-blue-500 bg-blue-50';
            default:
                return 'border-l-gray-500 bg-gray-50';
        }
    };

    const formatDueDate = (dueDate) => {
        if (!dueDate) return null;

        const date = new Date(dueDate);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { text: 'Overdue', color: 'text-red-600', icon: AlertCircle };
        } else if (diffDays === 0) {
            return { text: 'Due Today', color: 'text-orange-600', icon: Clock };
        } else if (diffDays === 1) {
            return { text: 'Due Tomorrow', color: 'text-orange-500', icon: Clock };
        } else if (diffDays <= 7) {
            return { text: `Due in ${diffDays} days`, color: 'text-yellow-600', icon: Clock };
        } else {
            return { text: `Due ${date.toLocaleDateString()}`, color: 'text-gray-600', icon: Clock };
        }
    };

    const handleDismiss = async () => {
        if (currentNotification) {
            setIsVisible(false);
            await dismissNotification(currentNotification.id);

            // Show next notification after a delay
            setTimeout(() => {
                setCurrentNotification(null);
            }, 300);
        }
    };

    const handleViewTask = async () => {
        if (currentNotification) {
            await markAsRead(currentNotification.id);
            setIsVisible(false);

            // Navigate to tasks page
            navigate('/employee/tasks');

            setTimeout(() => {
                setCurrentNotification(null);
            }, 300);
        }
    };

    const handleMarkAsRead = async () => {
        if (currentNotification) {
            setIsVisible(false);
            await markAsRead(currentNotification.id);

            setTimeout(() => {
                setCurrentNotification(null);
            }, 300);
        }
    };

    if (!currentNotification || !isVisible) return null;

    const dueInfo = formatDueDate(currentNotification.due_date);
    const DueIcon = dueInfo?.icon;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 pointer-events-none">
            <div
                className={`
                    pointer-events-auto w-full max-w-md
                    bg-white rounded-2xl shadow-2xl border-l-4
                    ${getPriorityColor(currentNotification.priority)}
                    transform transition-all duration-300 ease-out
                    ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
                `}
            >
                {/* Header */}
                <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-hover rounded-xl flex items-center justify-center shadow-lg">
                                {getTaskIcon(currentNotification.task_type)}
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">
                                    {currentNotification.title}
                                </h3>
                                <p className="text-sm text-gray-500">New Assignment</p>
                            </div>
                        </div>
                        <button
                            onClick={handleDismiss}
                            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={20} className="text-gray-400" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                            {currentNotification.task_title}
                        </h4>
                        {currentNotification.task_description && (
                            <p className="text-sm text-gray-600 line-clamp-2">
                                {currentNotification.task_description}
                            </p>
                        )}
                    </div>

                    {/* Task Details */}
                    <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                            <span className="px-2 py-1 bg-primary/10 text-primary rounded-lg font-medium capitalize">
                                {currentNotification.task_type}
                            </span>
                        </div>

                        {dueInfo && (
                            <div className={`flex items-center gap-1 ${dueInfo.color}`}>
                                {DueIcon && <DueIcon size={16} />}
                                <span className="font-medium">{dueInfo.text}</span>
                            </div>
                        )}
                    </div>

                    {/* Priority Badge */}
                    {currentNotification.priority && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Priority:</span>
                            <span className={`
                                px-2 py-1 rounded-lg text-xs font-semibold
                                ${currentNotification.priority === 'high' ? 'bg-red-100 text-red-700' : ''}
                                ${currentNotification.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : ''}
                                ${currentNotification.priority === 'low' ? 'bg-green-100 text-green-700' : ''}
                            `}>
                                {currentNotification.priority.toUpperCase()}
                            </span>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="p-4 bg-gray-50 rounded-b-2xl flex gap-2">
                    <button
                        onClick={handleViewTask}
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-primary to-primary-hover text-white rounded-xl font-semibold hover:from-primary-hover hover:to-primary-hover transition-all shadow-md hover:shadow-lg"
                    >
                        View Task
                    </button>
                    <button
                        onClick={handleMarkAsRead}
                        className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                    >
                        Later
                    </button>
                </div>

                {/* Progress indicator */}
                {popupNotifications.length > 1 && (
                    <div className="px-4 pb-3">
                        <div className="flex items-center justify-center gap-1">
                            {popupNotifications.slice(0, 5).map((_, index) => (
                                <div
                                    key={index}
                                    className={`h-1 rounded-full transition-all ${index === 0 ? 'w-6 bg-primary' : 'w-1 bg-gray-300'
                                        }`}
                                />
                            ))}
                            {popupNotifications.length > 5 && (
                                <span className="text-xs text-gray-500 ml-1">
                                    +{popupNotifications.length - 5}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskNotificationPopup;

